<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useSectionPermissions } from 'dashboard/composables/useSectionPermissions';

const { canEditSection } = useSectionPermissions();

const props = defineProps({
  client: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits([
  'configure',
  'upload',
  'send-now',
  'delete',
]);

const { t } = useI18n();

// Selected boleto for sending
const selectedBoletoId = ref(null);

const monthLabels = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

const boletosGrid = computed(() => {
  const grid = [];
  const currentYear = new Date().getFullYear();

  for (let month = 1; month <= 12; month++) {
    const refMonth = `${currentYear}-${month.toString().padStart(2, '0')}`;
    const boleto = props.client.boleto_files?.find(
      b => b.reference_month === refMonth
    );

    const sendCount = props.client.send_count || 1;
    const timesSent = boleto?.times_sent || 0;
    const isComplete = timesSent >= sendCount;
    const filePurged = boleto?.file_purged || false;

    grid.push({
      month,
      label: monthLabels[month - 1],
      boleto,
      status: boleto?.status || 'missing',
      // Permite selecionar boletos, exceto os que têm arquivo perdido ou excluído
      canSelect:
        boleto !== undefined &&
        boleto.status !== 'file_lost' &&
        !filePurged,
      sendInfo: boleto ? `${timesSent}/${sendCount}` : null,
      isComplete,
      filePurged,
    });
  }
  return grid;
});

const selectedBoleto = computed(() => {
  if (!selectedBoletoId.value) return null;
  return props.client.boleto_files?.find(b => b.id === selectedBoletoId.value);
});

const canSend = computed(() => {
  return selectedBoleto.value !== null;
});

const isResend = computed(() => {
  if (!selectedBoleto.value) return false;
  const sendCount = props.client.send_count || 1;
  return (selectedBoleto.value.times_sent || 0) >= sendCount;
});

const sendButtonLabel = computed(() => {
  if (!canSend.value) return t('OMNI.BOLETOS.SELECT_TO_SEND');
  if (isResend.value) return t('OMNI.BOLETOS.RESEND');
  return t('OMNI.BOLETOS.SEND_NOW');
});

const lastSentText = computed(() => {
  if (!props.client.last_sent_at) return t('OMNI.BOLETOS.NEVER_SENT');
  return formatDistanceToNow(new Date(props.client.last_sent_at), {
    addSuffix: true,
    locale: ptBR,
  });
});

const initials = computed(() => {
  return props.client.contact.name?.charAt(0).toUpperCase() || '?';
});

// Send schedule info
const sendScheduleText = computed(() => {
  const count = props.client.send_count || 1;
  const interval = props.client.send_interval_days || 2;
  const days = props.client.calculated_send_days || [props.client.send_day];
  
  if (count === 1) {
    return t('OMNI.BOLETOS.SINGLE_SEND_DAY', { day: props.client.send_day });
  }
  
  return t('OMNI.BOLETOS.MULTI_SEND_DAYS', { 
    count, 
    days: days.join(', '), 
    interval 
  });
});

const handleBoletoClick = (item) => {
  if (!item.canSelect) return;
  
  // Toggle selection
  if (selectedBoletoId.value === item.boleto.id) {
    selectedBoletoId.value = null;
  } else {
    selectedBoletoId.value = item.boleto.id;
  }
};

const handleSendNow = () => {
  if (!selectedBoletoId.value) return;
  emit('send-now', selectedBoletoId.value);
};

const clearSelection = () => {
  selectedBoletoId.value = null;
};

// Expose clearSelection for parent
defineExpose({ clearSelection });

// Get tooltip text for boleto
const getTooltipText = (item) => {
  if (!item.boleto) return t('OMNI.BOLETOS.NO_BOLETO');
  
  const boleto = item.boleto;
  
  // Special message for lost files
  if (boleto.status === 'file_lost') {
    return t('OMNI.BOLETOS.STATUS.FILE_LOST') + ' - ' + t('OMNI.BOLETOS.FILE_LOST_HINT');
  }
  
  const sendCount = props.client.send_count || 1;
  const timesSent = boleto.times_sent || 0;
  const sends = `${timesSent}/${sendCount}`;
  
  // Special message for completed sends with purged file
  if (item.filePurged) {
    return `${t('OMNI.BOLETOS.STATUS.COMPLETED')} - ${sends} - ${t('OMNI.BOLETOS.FILE_PURGED_HINT')}`;
  }
  
  const statusText = boleto.status === 'sent' 
    ? t('OMNI.BOLETOS.STATUS.SENT') 
    : boleto.status === 'failed' 
      ? t('OMNI.BOLETOS.STATUS.FAILED') 
      : t('OMNI.BOLETOS.STATUS.PENDING');
  
  return `${statusText} - ${sends} - ${t('OMNI.BOLETOS.CLICK_TO_SELECT')}`;
};
</script>

<template>
  <div
    class="bg-n-solid-2 rounded-xl border border-n-weak overflow-hidden shadow-sm hover:shadow-md transition-shadow"
  >
    <!-- Header -->
    <div
      class="flex items-center justify-between p-4 border-b border-n-weak"
    >
      <div class="flex items-center gap-3">
        <div
          class="w-12 h-12 rounded-full bg-gradient-to-br from-woot-400 to-woot-600 flex items-center justify-center text-white font-bold text-lg"
        >
          {{ initials }}
        </div>
        <div>
          <h3 class="font-semibold text-n-slate-12">
            {{ client.contact.name }}
          </h3>
          <div class="flex items-center gap-2 text-sm text-n-slate-11">
            <span class="i-lucide-smartphone" />
            {{ client.contact.phone_number }}
            <span class="text-n-slate-8">•</span>
            <span class="i-lucide-inbox" />
            {{ client.inbox.name }}
          </div>
        </div>
      </div>

      <div
        class="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
        :class="
          client.active
            ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
            : 'bg-n-alpha-3 text-n-slate-11'
        "
      >
        <span
          class="w-1.5 h-1.5 rounded-full"
          :class="client.active ? 'bg-green-500' : 'bg-n-slate-8'"
        />
        {{ client.active ? t('OMNI.BOLETOS.ACTIVE') : t('OMNI.BOLETOS.INACTIVE') }}
      </div>
    </div>

    <!-- Config Summary -->
    <div
      class="px-4 py-3 bg-n-alpha-2 text-sm"
    >
      <div class="flex items-center gap-4 text-n-slate-11 flex-wrap">
        <span class="flex items-center gap-1">
          <span class="i-lucide-calendar" />
          {{ sendScheduleText }}
        </span>
        <span class="flex items-center gap-1">
          <span class="i-lucide-clock" />
          {{ client.send_time }}
        </span>
        <span class="flex items-center gap-1">
          <span class="i-lucide-message-square" />
          {{
            client.custom_message
              ? t('OMNI.BOLETOS.CUSTOM_MESSAGE')
              : t('OMNI.BOLETOS.DEFAULT_MESSAGE')
          }}
        </span>
      </div>
    </div>

    <!-- Boletos Grid -->
    <div class="p-4">
      <div class="flex items-center justify-between mb-3">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('OMNI.BOLETOS.BOLETOS') }} {{ new Date().getFullYear() }}
          <span class="text-xs text-n-slate-10 ml-1">({{ t('OMNI.BOLETOS.CLICK_TO_SELECT') }})</span>
        </span>
        <button
          class="text-sm text-woot-500 hover:text-woot-600 font-medium flex items-center gap-1"
          @click="emit('upload')"
        >
          <span class="i-lucide-upload" />
          {{ t('OMNI.BOLETOS.UPLOAD') }}
        </button>
      </div>

      <div class="grid grid-cols-12 gap-1.5">
        <div
          v-for="item in boletosGrid"
          :key="item.month"
          class="relative group"
        >
          <div
            class="aspect-square rounded-md flex flex-col items-center justify-center text-[10px] font-medium transition-all"
            :class="[
              {
                'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400':
                  item.status === 'sent' && selectedBoletoId !== item.boleto?.id,
                'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400':
                  item.status === 'pending' && selectedBoletoId !== item.boleto?.id,
                'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400':
                  item.status === 'failed' && selectedBoletoId !== item.boleto?.id,
                'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400 ring-2 ring-orange-400':
                  item.status === 'file_lost' && selectedBoletoId !== item.boleto?.id,
                'bg-n-alpha-3 text-n-slate-10':
                  item.status === 'missing',
                'ring-2 ring-woot-500 bg-woot-100 text-woot-700 dark:bg-woot-900/40 dark:text-woot-400':
                  selectedBoletoId === item.boleto?.id,
              },
              item.canSelect && item.status !== 'file_lost' ? 'cursor-pointer hover:ring-2 hover:ring-woot-300' : 'cursor-default',
            ]"
            @click="handleBoletoClick(item)"
          >
            <!-- Month label -->
            <span class="leading-none font-semibold">{{ item.label }}</span>
            
            <!-- Send count indicator (1/2) -->
            <span 
              v-if="item.sendInfo"
              class="text-[8px] mt-0.5 leading-none opacity-75"
            >
              {{ item.sendInfo }}
            </span>
          </div>

          <!-- Tooltip -->
          <div
            v-if="item.boleto"
            class="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-1 bg-n-solid-3 text-n-slate-12 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-20 pointer-events-none shadow-lg"
          >
            {{ getTooltipText(item) }}
          </div>
        </div>
      </div>

      <!-- Selection indicator -->
      <div 
        v-if="selectedBoleto" 
        class="mt-3 p-2 rounded-lg flex items-center justify-between"
        :class="isResend 
          ? 'bg-blue-50 dark:bg-blue-900/30' 
          : 'bg-woot-50 dark:bg-woot-900/30'"
      >
        <span 
          class="text-sm"
          :class="isResend 
            ? 'text-blue-700 dark:text-blue-300' 
            : 'text-woot-700 dark:text-woot-300'"
        >
          <span class="i-lucide-check-circle mr-1" />
          <template v-if="isResend">
            {{ t('OMNI.BOLETOS.RESEND_LABEL') }}: <strong>{{ selectedBoleto.month_label }}</strong>
            ({{ t('OMNI.BOLETOS.ALREADY_SENT', { count: selectedBoleto.times_sent || 0 }) }})
          </template>
          <template v-else>
            {{ t('OMNI.BOLETOS.SELECTED_LABEL') }}: <strong>{{ selectedBoleto.month_label }}</strong>
            ({{ selectedBoleto.times_sent || 0 }}/{{ client.send_count || 1 }} {{ t('OMNI.BOLETOS.SENDS') }})
          </template>
        </span>
        <button 
          class="text-xs text-woot-500 hover:text-woot-600"
          @click="selectedBoletoId = null"
        >
          {{ t('OMNI.BOLETOS.CLEAR') }}
        </button>
      </div>
    </div>

    <!-- Stats -->
    <div
      class="px-4 py-3 border-t border-n-weak flex items-center justify-between text-sm"
    >
      <div class="flex items-center gap-4 text-n-slate-11">
        <span class="flex items-center gap-1">
          <span class="i-lucide-check-circle text-green-500" />
          {{ client.sent_count }} {{ t('OMNI.BOLETOS.SENT') }}
        </span>
        <span class="flex items-center gap-1">
          <span class="i-lucide-clock text-yellow-500" />
          {{ client.pending_count }} {{ t('OMNI.BOLETOS.PENDING') }}
        </span>
        <span class="flex items-center gap-1">
          <span class="i-lucide-send text-blue-500" />
          {{ lastSentText }}
        </span>
      </div>
    </div>

    <!-- Actions -->
    <div
      class="px-4 py-3 border-t border-n-weak flex items-center gap-2"
    >
      <button
        v-if="canEditSection('boletos')"
        class="flex-1 px-3 py-2 text-sm font-medium text-n-slate-12 bg-n-alpha-3 rounded-lg hover:bg-n-slate-4 flex items-center justify-center gap-2 transition-colors"
        @click="emit('configure')"
      >
        <span class="i-lucide-settings" />
        {{ t('OMNI.BOLETOS.CONFIGURE') }}
      </button>
      <button
        class="flex-1 px-3 py-2 text-sm font-medium rounded-lg flex items-center justify-center gap-2 transition-colors"
        :class="{
          'text-white bg-n-blue-9 hover:bg-n-blue-10': canSend && isResend,
          'text-white bg-woot-500 hover:bg-woot-600': canSend && !isResend,
          'text-n-slate-10 bg-n-alpha-3 cursor-not-allowed': !canSend
        }"
        :disabled="!canSend"
        :title="canSend ? (isResend ? t('OMNI.BOLETOS.RESEND_TITLE') : t('OMNI.BOLETOS.SEND_TITLE')) : t('OMNI.BOLETOS.SELECT_BOLETO_HINT')"
        @click="handleSendNow"
      >
        <span :class="isResend ? 'i-lucide-rotate-ccw' : 'i-lucide-send'" />
        {{ sendButtonLabel }}
      </button>
      <button
        v-if="canEditSection('boletos')"
        class="p-2 text-n-slate-10 hover:text-red-500 rounded-lg bg-n-alpha-3 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
        :title="t('OMNI.BOLETOS.DELETE_CLIENT')"
        @click="emit('delete')"
      >
        <span class="i-lucide-trash-2" />
      </button>
    </div>
  </div>
</template>
